import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSystemHealth } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { Shield, Wifi, Database, Activity, Check } from "lucide-react";
import { isStateStoreEnabled } from "@/state/store";
import { useSystemHealth, useSystemHealthIsStale, useSystemHealthLastUpdated } from "@/state/hooks/useSystemHealthState";

/**
 * Compact health strip rendered as a sub-bar below the TopBar.
 *
 * Shows at a glance: Kite token · WS feed · Market data · DB · Scheduler.
 * Each chip is green / amber / red based on the backend SystemHealth check.
 * Hovering shows the underlying reason. The Emergency-stop button already
 * lives in TopBar — no duplicate here.
 */

type Tone = "ok" | "warn" | "err";

function Chip({ icon, label, tone, title }: { icon: React.ReactNode; label: string; tone: Tone; title?: string }) {
  const color = tone === "ok"
    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
    : tone === "warn"
    ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
    : "bg-red-500/15 text-red-300 border-red-500/40";
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${color}`}
    >
      {icon}
      <span>{label}</span>
    </span>
  );
}

/** True when current IST time is within NSE trading hours (Mon-Fri 09:15-15:30 IST). */
function isMarketOpenIST(): boolean {
  const now = new Date();
  // IST = UTC+5:30 (no DST)
  const istMs = now.getTime() + (5.5 * 3600 * 1000) + (now.getTimezoneOffset() * 60 * 1000);
  const ist = new Date(istMs);
  const day = ist.getUTCDay(); // 0=Sun, 6=Sat — using UTC getters because we already shifted
  if (day === 0 || day === 6) return false;
  const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
  return mins >= (9 * 60 + 15) && mins <= (15 * 60 + 30);
}

// Backend refreshes system_health every 60s; warn the operator after this
// many seconds of no update. Tuned past 2× refresh interval to absorb a
// single missed broadcast without crying wolf.
const STALE_AGE_SECS = 150;

export default function HealthStrip() {
  // Phase B.1: prefer state-store snapshot over the polled /api/health/system.
  // Falls back to legacy useQuery when state store is opted out.
  const _storeOn = isStateStoreEnabled();
  const _storeHealth = useSystemHealth();
  const _storeIsStale = useSystemHealthIsStale();
  const _storeLastUpdated = useSystemHealthLastUpdated();
  const { data: legacyHealth, dataUpdatedAt: legacyUpdatedAt } = useQuery({
    queryKey: queryKeys.systemHealth,
    queryFn: fetchSystemHealth,
    staleTime: 20_000,
    enabled: !_storeOn,
  });
  const health: any = _storeOn ? _storeHealth : legacyHealth;

  // Re-tick every 30s so stale-age renders advance without waiting for a snapshot.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!health) return null;

  const lastUpdatedAt = _storeOn ? _storeLastUpdated : legacyUpdatedAt;
  const ageSecs = lastUpdatedAt > 0 ? Math.floor((Date.now() - lastUpdatedAt) / 1000) : 0;
  const isStale = _storeOn ? (_storeIsStale || ageSecs > STALE_AGE_SECS) : ageSecs > STALE_AGE_SECS;
  const ageLabel = ageSecs < 60
    ? `${ageSecs}s ago`
    : ageSecs < 3600
    ? `${Math.floor(ageSecs / 60)}m ago`
    : `${Math.floor(ageSecs / 3600)}h ago`;
  const c = health.checks;
  const toneFor = (ok: boolean | undefined): Tone => (ok ? "ok" : "err");

  // Market-aware tones for the data-flow chips. WS being "connected" or
  // Feed having a subscription count doesn't mean data is flowing — it
  // just means the socket is up. Off-hours, both stay subscribed but
  // produce zero ticks. Show amber to reflect that distinction.
  const marketOpen = isMarketOpenIST();
  const wsTone: Tone = !c.websocket.kite_ws_live
    ? "err"
    : marketOpen ? "ok" : "warn";
  const feedTone: Tone = !c.market_feed.live
    ? "err"
    : marketOpen ? "ok" : "warn";
  const offHoursSuffix = marketOpen ? "" : " · idle";

  return (
    <div className={`flex items-center gap-1.5 flex-wrap px-3 sm:px-5 py-1 border-b border-zinc-800/40 bg-[#0a0a10] shrink-0 ${isStale ? "opacity-60" : ""}`}>
      <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-semibold mr-1">Health</span>
      <Chip
        icon={<Shield size={11} />}
        label={c.kite.authenticated ? "Kite" : "Kite ✗"}
        tone={c.kite.authenticated && c.kite.token_valid !== false ? "ok" : "err"}
        title={c.kite.detail || `token_valid=${c.kite.token_valid}`}
      />
      <Chip
        icon={<Wifi size={11} />}
        label={c.websocket.kite_ws_live ? `WS ${c.websocket.subscriptions}` : "WS ✗"}
        tone={wsTone}
        title={`${c.websocket.detail}${offHoursSuffix ? "  ·  market closed (no ticks flowing)" : ""}`}
      />
      <Chip
        icon={<Activity size={11} />}
        label={c.market_feed.live ? `Feed ${c.market_feed.tokens_subscribed}` : "Feed ✗"}
        tone={feedTone}
        title={`${c.market_feed.detail}${offHoursSuffix ? "  ·  market closed (no ticks flowing)" : ""}`}
      />
      <Chip
        icon={<Database size={11} />}
        label="DB"
        tone={toneFor(c.database.ok)}
        title={c.database.detail}
      />
      <Chip
        icon={<Check size={11} />}
        label={`Jobs ${c.scheduler.active_jobs}/${c.scheduler.total_jobs}`}
        tone={c.scheduler.running ? "ok" : "err"}
        title={c.scheduler.detail}
      />
      {!marketOpen && (
        <span
          className="text-[10px] text-zinc-500 ml-1"
          title="NSE trading hours: Mon–Fri 09:15–15:30 IST"
        >
          · Market closed
        </span>
      )}
      {isStale && (
        <span
          className="text-[10px] font-semibold text-amber-400 ml-1"
          title={`Snapshot is ${ageLabel} — backend refreshes every 60s. Hard-refresh (Cmd+Shift+R) if this persists.`}
        >
          · stale {ageLabel}
        </span>
      )}
    </div>
  );
}
